import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import signupForFreeLogo from './signupForFree.svg';
import css from './SectionHero.module.css';

const CATEGORY_MAP = {
  'Farriery & Hoof Care': 'farriery_hoof_care_s',
  'Veterinary & Dental': 'veterinary_dental_s',
  'Physiotherapy & Bodywork': 'physiotherapy_bodywork_s',
  'Saddle & Tack': 'saddlery_tack_s',
  'Coaching & Behaviour': 'coaching_behaviour_s',
  'Grooming & Clipping': 'grooming_clipping_s',
  'Livery, Yard & Stable': 'livery_yard_stable_s',
  'Breeding & Stud': 'breeding_stud_s',
  'Transport, Feed&Nutrition': 'transport_feed_nutrition_s',
  'Other & Specialist Services': 'other_specialist_services_s',
};

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }

  const history = useHistory();
  const location = useLocation();

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields(
    [title, description, callToAction],
    fieldOptions
  );

  const [selectedCategories, setSelectedCategories] = useState([]);

  const toggleCategory = categoryKey => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryKey)) {
        return prev.filter(c => c !== categoryKey);
      }
      return [...prev, categoryKey];
    });
  };

  const onSearchClick = () => {
    const params = new URLSearchParams(location.search);
    params.set('listingType', 'services');
    params.set('pub_listingType', 'services');

    if (selectedCategories.length > 0) {
      const mappedValues = selectedCategories
        .map(cat => CATEGORY_MAP[cat])
        .filter(Boolean)
        .join(',');

      params.set('pub_categoryForServices', `has_all:${mappedValues}`);
    } else {
      params.delete('pub_categoryForServices');
    }

    history.push(`/s?${params.toString()}`);
  };

  const splitIntoRows = (items, rows = 3) => {
    const result = Array.from({ length: rows }, () => []);
    items.forEach((item, index) => {
      result[index % rows].push(item);
    });
    return result;
  };

  return (<>
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          {isMobile && (
            <>
              <div className={css.mobilePillStrip}>
                {splitIntoRows(Object.keys(CATEGORY_MAP), 3).map((row, rowIndex) => (
                  <div key={rowIndex} className={css.categoryFilterMobile}>
                    {row.map(category => (
                      <button
                        key={category}
                        type="button"
                        className={classNames(
                          css.filterPillMobile,
                          selectedCategories.includes(category) && css.active
                        )}
                        onClick={() => toggleCategory(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                ))}
              </div>

              {selectedCategories.length > 0 && (
                <button
                  type="button"
                  className={defaultClasses.ctaButton}
                  onClick={onSearchClick}
                >
                  Search
                </button>
              )}
            </>
          )}

          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />

          {!isMobile &&
            <>
              <div className={css.categoryFilter}>
                {Object.keys(CATEGORY_MAP).map(category => (
                  <button
                    key={category}
                    type="button"
                    className={classNames(
                      css.filterPill,
                      selectedCategories.includes(category) && css.active
                    )}
                    onClick={() => toggleCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {selectedCategories.length > 0 &&
                <button
                  type="button"
                  className={defaultClasses.ctaButton}
                  onClick={onSearchClick}
                >
                  Search
                </button>
              }
            </>
          }
        </header>
      ) : null}
    </SectionContainer>
    <div className={css.signupForFreeLayer}>
      <img
        src={signupForFreeLogo}
        className={css.signupForFreeLogo}
      />
    </div>
  </>);
};

export default SectionHero;
